<template>
  <v-col class="pa-6">
    <div v-if="!viewingReport">
      <v-row class="ma-0 mb-4">
        <v-col cols="12" md="3">
          <v-text-field
            class="input"
            label="Search..."
            prepend-inner-icon="mdi-magnify"
            v-model="search"
            rounded
            :hide-details="true"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            class="input"
            label="Urgency"
            :items="dangerOptions"
            v-model="selectedDanger"
            item-text="text"
            item-value="value"
            rounded
            :hide-details="true"
          ></v-select>
        </v-col>
      </v-row>
      <v-row justify="center">
        <v-data-table
          style="width: 100%"
          :headers="headers"
          :items="filteredReports"
          :items-per-page="10"
          class="elevation-1 mt-4 ml-4 mr-8"
          no-data-text="No abuse reports found"
        >
          <template v-slot:item.clientName="{ item }">
            <td>{{ item.clientName || "Unknown" }}</td>
          </template>
          <template v-slot:item.incidentTypes="{ item }">
            <td>{{ formatIncidentTypes(item.incidentTypes) }}</td>
          </template>
          <template v-slot:item.submittedAt="{ item }">
            <td>{{ formatTimestamp(item.submittedAt) }}</td>
          </template>
          <template v-slot:item.status="{ item }">
            <td>
              <v-chip
                class="capatilize"
                :color="item.isActive ? 'green' : 'orange'"
                dark
                small
              >
                {{ item.isActive ? "Active" : "Archived" }}
              </v-chip>
            </td>
          </template>
          <template v-slot:item.danger="{ item }">
            <td>
              <v-chip
                class="capatilize"
                :color="dangerChipColor(item.immediateDanger)"
                dark
              >
                {{ item.immediateDanger ? "Immediate" : "Not Immediate" }}
              </v-chip>
            </td>
          </template>
          <template v-slot:item.action="{ item }">
            <td>
              <v-icon color="#4390c0" @click="viewReport(item)">mdi-eye</v-icon>
            </td>
          </template>
        </v-data-table>
      </v-row>
    </div>
    <div v-else>
      <v-row class="ma-2">
        <v-col cols="12" md="6">
          <v-row class="mb-2">
            <v-icon class="black-color" @click="close()">mdi-arrow-left</v-icon>
            <h4 class="grey-color mx-4" style="font-weight: 500">
              Abuse Report
            </h4>
          </v-row>
        </v-col>
      </v-row>
      <v-row justify="center">
        <v-col cols="12" md="8">
          <div class="white-card pa-8">
            <v-row justify="space-between" class="ma-0">
              <div>
                <h2 class="fs400 mb-2">
                  {{ selectedReport.clientName || "Unknown Client" }}
                </h2>
                <p class="grey-color">
                  Submitted {{ formatTimestamp(selectedReport.submittedAt) }}
                </p>
              </div>
              <div class="text-right">
                <v-chip
                  class="status-chip mx-2 mt-1"
                  :color="dangerChipColor(selectedReport.immediateDanger)"
                  dark
                >
                  {{ selectedReport.immediateDanger ? "Immediate Danger" : "Not Immediate" }}
                </v-chip>
              </div>
            </v-row>
            <v-row class="mt-6">
              <v-col cols="12" md="4">
                <p class="info-label">Email</p>
                <p class="info-value">
                  {{ selectedReport.clientEmail || "N/A" }}
                </p>
              </v-col>
              <v-col cols="12" md="4">
                <p class="info-label">Phone</p>
                <p class="info-value">
                  {{ selectedReport.clientPhone || "N/A" }}
                </p>
              </v-col>
              <v-col cols="12" md="4">
                <p class="info-label">Address</p>
                <p class="info-value">
                  {{ selectedReport.clientAddress || "N/A" }}
                </p>
              </v-col>
            </v-row>
            <v-row class="mt-4">
              <v-col cols="12" md="4">
                <p class="info-label">Incident Date</p>
                <p class="info-value">
                  {{
                    selectedReport.incidentDate
                      ? selectedReport.incidentDate
                      : "Not provided"
                  }}
                </p>
              </v-col>
              <v-col cols="12" md="4">
                <p class="info-label">Location</p>
                <p class="info-value">
                  {{ selectedReport.location || "Not provided" }}
                </p>
              </v-col>
              <v-col cols="12" md="4">
                <p class="info-label">Relationship</p>
                <p class="info-value">
                  {{ selectedReport.relationship || "Not provided" }}
                </p>
              </v-col>
            </v-row>
            <v-row class="mt-4">
              <v-col cols="12" md="6">
                <p class="info-label">Incident Types</p>
                <div class="d-flex flex-wrap">
                  <v-chip
                    v-for="type in selectedReport.incidentTypes || []"
                    :key="type"
                    class="ma-1"
                    color="#4390c0"
                    text-color="white"
                    small
                  >
                    {{ type }}
                  </v-chip>
                  <p v-if="!selectedReport.incidentTypes || !selectedReport.incidentTypes.length" class="info-value">
                    Not provided
                  </p>
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <p class="info-label">Safe Contact</p>
                <p class="info-value">
                  {{ selectedReport.safeContact || "Not provided" }}
                </p>
              </v-col>
            </v-row>
            <v-row class="mt-4">
              <v-col cols="12" md="6">
                <p class="info-label">Incident Description</p>
                <p class="info-value break-word">
                  {{ selectedReport.incidentDescription || selectedReport.summary || "No description provided" }}
                </p>
              </v-col>
              <v-col cols="12" md="6">
                <p class="info-label">Additional Information</p>
                <p class="info-value break-word">
                  {{ selectedReport.additionalInfo || selectedReport.previousIncidents || "Not provided" }}
                </p>
              </v-col>
            </v-row>
            <v-row class="mt-4" v-if="selectedReport.incidentTime">
              <v-col cols="12" md="6">
                <p class="info-label">Incident Time</p>
                <p class="info-value">
                  {{ selectedReport.incidentTime || "Not provided" }}
                </p>
              </v-col>
            </v-row>
            <v-row class="mt-4">
              <v-col cols="12" md="4">
                <p class="info-label">Children Impacted</p>
                <v-chip :color="selectedReport.childrenImpacted ? '#DC143C' : '#2e7d32'" dark>
                  {{ selectedReport.childrenImpacted ? "Yes" : "No" }}
                </v-chip>
              </v-col>
              <v-col cols="12" md="4">
                <p class="info-label">Immediate Danger</p>
                <v-chip :color="selectedReport.immediateDanger ? '#DC143C' : '#2e7d32'" dark>
                  {{ selectedReport.immediateDanger ? "Yes" : "No" }}
                </v-chip>
              </v-col>
              <v-col cols="12" md="4">
                <p class="info-label">Platform Timezone</p>
                <p class="info-value">
                  {{ selectedReport.platformMeta?.timezone || "Not provided" }}
                </p>
              </v-col>
            </v-row>
            <v-row class="mt-4">
              <v-col cols="12">
                <p class="info-label">User Agent</p>
                <p class="info-value break-word">
                  {{ selectedReport.platformMeta?.userAgent || "Not provided" }}
                </p>
              </v-col>
            </v-row>
            <v-row v-if="selectedReport.proofUrls && selectedReport.proofUrls.length > 0" class="mt-4">
              <v-col cols="12">
                <p class="info-label mb-2">Proof Files ({{ selectedReport.proofCount || selectedReport.proofUrls.length }})</p>
                <div class="d-flex flex-wrap">
                  <v-card
                    v-for="(url, index) in selectedReport.proofUrls"
                    :key="index"
                    class="ma-2"
                    max-width="200"
                    elevation="2"
                  >
                    <v-img
                      v-if="isImage(url)"
                      :src="url"
                      height="150"
                      @click="viewImage(url)"
                      style="cursor: pointer"
                    ></v-img>
                    <v-card-text v-else>
                      <v-icon>mdi-file</v-icon>
                      <a :href="url" target="_blank" class="ml-2">View File</a>
                    </v-card-text>
                  </v-card>
                </div>
              </v-col>
            </v-row>
          </div>
        </v-col>
      </v-row>
      <!-- Chat Section -->
      <v-row justify="center" class="mt-4">
        <v-col cols="12" md="8">
          <div class="white-card pa-4">
            <v-row class="ma-0 mb-2">
              <v-col cols="12">
                <h3 class="fs400">Messages</h3>
                <v-chip
                  v-if="!selectedReport.isActive"
                  class="mt-2"
                  color="orange"
                  dark
                  small
                >
                  Archived Report - Messaging Disabled
                </v-chip>
                <v-chip
                  v-else
                  class="mt-2"
                  color="green"
                  dark
                  small
                >
                  Active Report
                </v-chip>
              </v-col>
            </v-row>
            <v-row class="ma-0">
              <v-col cols="12">
                <div class="chat-container" ref="chatContainer">
                  <div v-if="!messages || messages.length === 0" class="text-center pa-8 grey-color">
                    No messages yet. Start the conversation by sending a message.
                  </div>
                  <div
                    v-for="(message, index) in sortedMessages"
                    :key="index"
                    class="message-wrapper"
                  >
                    <div
                      v-if="shouldShowDateSeparator(message, index)"
                      class="date-separator"
                    >
                      {{ formatMessageDate(message.date) }}
                    </div>
                    <div
                      :class="[
                        'message-bubble',
                        message.sender === 'client' ? 'client-message' : 'support-message'
                      ]"
                    >
                      <p class="message-text">{{ message.message }}</p>
                      <span class="message-time">{{ message.time }}</span>
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
            <v-row class="ma-0 mt-2" v-if="selectedReport.isActive">
              <v-col cols="12">
                <v-textarea
                  v-model="newMessage"
                  placeholder="Type your message..."
                  rows="3"
                  outlined
                  dense
                  :disabled="sendingMessage"
                  @keydown.ctrl.enter="sendMessage"
                  @keydown.meta.enter="sendMessage"
                ></v-textarea>
                <v-btn
                  color="#4390c0"
                  dark
                  rounded
                  :disabled="!newMessage.trim() || sendingMessage"
                  :loading="sendingMessage"
                  @click="sendMessage"
                  class="mt-2"
                >
                  <v-icon left>mdi-send</v-icon>
                  Send Message
                </v-btn>
              </v-col>
            </v-row>
            <v-row v-else class="ma-0 mt-2">
              <v-col cols="12">
                <v-alert type="warning" dense>
                  This report is archived. A newer report exists for this client. Messaging is disabled.
                </v-alert>
              </v-col>
            </v-row>
          </div>
        </v-col>
      </v-row>
    </div>
    <v-row justify="center">
      <v-overlay :value="loading">
        <v-progress-circular :size="100" color="#4390c0" indeterminate />
      </v-overlay>
    </v-row>
    <!-- Image Viewer Dialog -->
    <v-dialog v-model="imageViewerDialog" max-width="90%" @click:outside="imageViewerDialog = false">
      <v-card>
        <v-card-title class="d-flex justify-space-between">
          <span>Proof Image</span>
          <v-btn icon @click="imageViewerDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-0">
          <img :src="imageViewerUrl" style="width: 100%; height: auto; max-height: 80vh; object-fit: contain" />
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-col>
</template>

<script>
import {
  collection,
  doc,
  getDoc,
  getFirestore,
  onSnapshot,
  query,
  updateDoc,
} from "firebase/firestore";
import { firebaseApp } from "../../firebaseConfig";

const db = getFirestore(firebaseApp);

export default {
  name: "AbuseReports",
  data() {
    return {
      headers: [
        { text: "Client", value: "clientName" },
        { text: "Email", value: "clientEmail" },
        { text: "Phone", value: "clientPhone" },
        { text: "Incidents", value: "incidentTypes", sortable: false },
        { text: "Status", value: "status" },
        { text: "Urgency", value: "danger" },
        { text: "Submitted", value: "submittedAt" },
        { text: "Action", value: "action", sortable: false },
      ],
      dangerOptions: [
        { text: "All", value: "all" },
        { text: "Immediate Danger", value: "danger" },
        { text: "Not Immediate", value: "safe" },
      ],
      reports: [],
      search: "",
      selectedDanger: "all",
      viewingReport: false,
      selectedReport: null,
      cachedReportId: null,
      loading: false,
      unsubscribeReports: null,
      messages: [],
      newMessage: "",
      sendingMessage: false,
      unsubscribeMessages: null,
      imageViewerUrl: null,
      imageViewerDialog: false,
    };
  },
  created() {
    this.cachedReportId = localStorage.getItem("ViewedAbuseReport");
    this.getReports();
  },
  beforeDestroy() {
    if (this.unsubscribeReports) {
      this.unsubscribeReports();
    }
    if (this.unsubscribeMessages) {
      this.unsubscribeMessages();
    }
  },
  computed: {
    filteredReports() {
      const searchTerm = this.search.toLowerCase();
      return this.reports
        .filter((report) => {
          if (this.selectedDanger === "all") return true;
          if (this.selectedDanger === "danger") return report.immediateDanger;
          if (this.selectedDanger === "safe") return !report.immediateDanger;
          return true;
        })
        .filter((report) => {
          if (!searchTerm) return true;
          const haystack = [
            report.clientName,
            report.clientEmail,
            report.clientPhone,
            report.summary,
            ...(report.incidentTypes || []),
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return haystack.includes(searchTerm);
        });
    },
    sortedMessages() {
      if (!this.messages || !this.messages.length) return [];
      return [...this.messages].sort((a, b) => {
        const dateA = a.fullDate?.toDate?.() || new Date(a.fullDate || a.date);
        const dateB = b.fullDate?.toDate?.() || new Date(b.fullDate || b.date);
        return dateA - dateB;
      });
    },
  },
  methods: {
    getReports() {
      try {
        const q = query(collection(db, "abuseReported"));
        this.loading = true;
        this.unsubscribeReports = onSnapshot(
          q,
          (snapshot) => {
            const reports = [];
            snapshot.forEach((docSnap) => {
              const data = docSnap.data();
              reports.push({
                id: docSnap.id,
                ...data,
                clientName: data.clientName || "Unknown",
                clientEmail: data.clientEmail || data.email || null,
                clientPhone: data.clientPhone || data.phone || null,
                incidentTypes: data.incidentTypes || [],
                submittedAt: data.submittedAt || null,
                isActive: data.isActive !== false,
                messages: data.messages || [],
                proofUrls: data.proofUrls || [],
                proofCount: data.proofCount || (data.proofUrls ? data.proofUrls.length : 0),
              });
            });
            this.reports = reports;
            this.loading = false;

            if (this.cachedReportId) {
              const cached = this.reports.find(
                (report) => report.id === this.cachedReportId
              );
              if (cached) {
                this.viewReport(cached);
              } else {
                localStorage.removeItem("ViewedAbuseReport");
                this.cachedReportId = null;
              }
            }
          },
          (error) => {
            console.error("Error fetching abuse reports:", error);
            this.loading = false;
          }
        );
      } catch (error) {
        this.loading = false;
        console.error("Error setting up abuse reports listener:", error);
      }
    },
    viewReport(report) {
      this.selectedReport = report;
      this.viewingReport = true;
      this.cachedReportId = report.id;
      localStorage.setItem("ViewedAbuseReport", report.id);
      this.messages = report.messages || [];
      this.setupMessageListener(report.id);
    },
    close() {
      if (this.unsubscribeMessages) {
        this.unsubscribeMessages();
        this.unsubscribeMessages = null;
      }
      this.viewingReport = false;
      this.selectedReport = null;
      this.cachedReportId = null;
      this.messages = [];
      this.newMessage = "";
      localStorage.removeItem("ViewedAbuseReport");
    },
    setupMessageListener(reportId) {
      if (this.unsubscribeMessages) {
        this.unsubscribeMessages();
      }
      
      const reportRef = doc(db, "abuseReported", reportId);
      this.unsubscribeMessages = onSnapshot(
        reportRef,
        (docSnapshot) => {
          if (docSnapshot.exists()) {
            const reportData = docSnapshot.data();
            this.messages = reportData.messages || [];
            this.selectedReport = {
              ...this.selectedReport,
              ...reportData,
              isActive: reportData.isActive !== false,
            };
            this.$nextTick(() => {
              this.scrollToBottom();
            });
          }
        },
        (error) => {
          console.error("Error listening to messages:", error);
        }
      );
    },
    async sendMessage() {
      if (!this.newMessage.trim() || !this.selectedReport?.id) return;
      
      // Check if report is active
      if (this.selectedReport.isActive === false) {
        alert("This report is archived. A newer report exists for this client.");
        return;
      }
      
      try {
        this.sendingMessage = true;
        
        // Get current time
        const now = new Date();
        const hours = String(now.getHours()).padStart(2, "0");
        const minutes = String(now.getMinutes()).padStart(2, "0");
        const time = `${hours}:${minutes}`;
        const date = now.toISOString().substring(0, 10);
        
        // Create new message object
        const newMessageObj = {
          message: this.newMessage.trim(),
          sender: "support",
          time: time,
          date: date,
          fullDate: now,
        };
        
        // Get current messages array
        const reportRef = doc(db, "abuseReported", this.selectedReport.id);
        const reportDoc = await getDoc(reportRef);
        const currentMessages = reportDoc.exists()
          ? reportDoc.data().messages || []
          : [];
        
        // Add new message
        currentMessages.push(newMessageObj);
        
        // Update document
        await updateDoc(reportRef, {
          messages: currentMessages,
        });
        
        this.newMessage = "";
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      } catch (error) {
        console.error("Error sending message:", error);
        alert("Failed to send message. Please try again.");
      } finally {
        this.sendingMessage = false;
      }
    },
    scrollToBottom() {
      if (this.$refs.chatContainer) {
        this.$refs.chatContainer.scrollTop = this.$refs.chatContainer.scrollHeight;
      }
    },
    shouldShowDateSeparator(message, index) {
      if (index === 0) return true;
      const prevMessage = this.sortedMessages[index - 1];
      return prevMessage.date !== message.date;
    },
    formatMessageDate(dateString) {
      if (!dateString) return "";
      try {
        const date = new Date(dateString);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        
        if (date.toDateString() === today.toDateString()) {
          return "Today";
        } else if (date.toDateString() === yesterday.toDateString()) {
          return "Yesterday";
        } else {
          return date.toLocaleDateString("en-US", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
          });
        }
      } catch (error) {
        return dateString;
      }
    },
    isImage(url) {
      if (!url) return false;
      return /\.(jpg|jpeg|png|gif|webp)$/i.test(url) || url.includes("image");
    },
    viewImage(url) {
      this.imageViewerUrl = url;
      this.imageViewerDialog = true;
    },
    formatTimestamp(timestamp) {
      if (!timestamp) return "No timestamp";
      try {
        if (timestamp.toDate) {
          return timestamp.toDate().toLocaleString();
        }
        if (
          typeof timestamp === "object" &&
          typeof timestamp.seconds === "number"
        ) {
          return new Date(timestamp.seconds * 1000).toLocaleString();
        }
        return new Date(timestamp).toLocaleString();
      } catch (error) {
        return timestamp;
      }
    },
    formatIncidentTypes(types) {
      if (!types || !types.length) return "Not provided";
      return types.join(", ");
    },
    dangerChipColor(isImmediate) {
      return isImmediate ? "#DC143C" : "#4390c0";
    },
  },
};
</script>

<style scoped>
.info-label {
  font-weight: 600;
  color: grey;
}

.info-value {
  font-size: 16px;
  font-weight: 500;
}

.break-word {
  word-break: break-word;
  white-space: pre-line;
}

.chat-container {
  max-height: 500px;
  overflow-y: auto;
  padding: 16px;
  background-color: #f5f5f5;
  border-radius: 8px;
}

.message-wrapper {
  margin-bottom: 16px;
}

.date-separator {
  text-align: center;
  color: grey;
  font-size: 12px;
  margin: 16px 0;
  font-weight: 500;
}

.message-bubble {
  max-width: 70%;
  padding: 12px 16px;
  border-radius: 16px;
  margin-bottom: 8px;
  position: relative;
}

.client-message {
  background: linear-gradient(135deg, #4390c0 0%, #5fb7d3 100%);
  color: white;
  margin-left: auto;
  text-align: right;
}

.support-message {
  background: white;
  color: #111b21;
  border: 1px solid #e0e0e0;
  margin-right: auto;
}

.message-text {
  margin: 0;
  word-wrap: break-word;
  font-size: 14px;
  line-height: 1.5;
}

.client-message .message-text {
  color: white;
}

.support-message .message-text {
  color: #111b21;
}

.message-time {
  font-size: 11px;
  opacity: 0.7;
  display: block;
  margin-top: 4px;
}

.client-message .message-time {
  text-align: right;
  color: white;
}

.support-message .message-time {
  text-align: left;
  color: #666;
}
</style>


